// Lion Auto: a group's "needs approval" queue (our listings that joined it
// after it was built). Approve (smart) slots each one in at its Smart sort
// spot, read from the event's Skybox pricing: just before the first ranked
// listing that sorts after it, the ranks after it moving up one; an approved
// listing with no listing in Skybox's read goes last (the answer's
// placedLast). At rank… sends the listing shown at the rank typed (beforeId:
// it goes just before it), or nothing for the last rank (appended: nothing
// else moves). The words the confirm and toast use, and which failures mean
// the page's copy is stale; pure, tested in lionAutoPending.test.js.
import { shouldRefreshOn } from './lionAutoChecker';

const NOT_IN_SKYBOX = "not in Skybox's current listings";

// A whole number from 1 (a number or its text), else null
const wholeFrom1 = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

const smartSpot = (n) => (n === 1 ? 'at its Smart sort spot' : 'at their Smart sort spots');

// How many approved ids went last for want of a Skybox listing: distinct ids, at most `count`
function placedLastCount(placedLast, count) {
  if (!Array.isArray(placedLast)) return 0;
  const ids = new Set(placedLast.filter(v => v !== null && v !== undefined && v !== '').map(String));
  return Math.min(ids.size, count);
}

// The Smart spot's toast: where they landed, and those that went last
function smartLandedText(count, landed, placedLast) {
  const last = placedLastCount(placedLast, count);
  if (landed) return `at rank ${landed} (${last ? `ranked last: ${NOT_IN_SKYBOX}` : 'its Smart sort spot'})`;
  if (!last) return smartSpot(count);
  if (last === count) return `${count === 1 ? '' : `all ${count} `}ranked last: ${NOT_IN_SKYBOX}`;
  return `${count - last} ${smartSpot(count - last)}; ${last} ranked last: ${NOT_IN_SKYBOX}`;
}

/**
 * Where an approve puts the listings, in words. Without an answer, for the
 * confirm asked first (a group on Auto): where they go and which ranks move
 * ("at rank 4: ranks 4 and up move up one and are repriced too"). With the
 * server's answer, for the toast: where they landed ("at rank 4 (its Smart
 * sort spot)", "at rank 6 (the ranking had changed)", "2 at their Smart sort
 * spots; 1 ranked last: not in Skybox's current listings").
 * The mode follows what the request carries: smart = each at its Smart sort
 * spot; else beforeId = just before that ranked listing (a rank typed in the
 * middle); else the end (the last rank typed: nothing else moves).
 * @param {{ count: number, smart?: boolean, beforeId?: number|null, rank?: number|null }} [request]
 *   count: the listings (asked, or approved once answered); rank: the rank typed (At rank…)
 * @param {{ landed?: number|null, placedLast?: Array<number|string> }|null} [answer]
 *   landed: the rank the one listing approved holds now, as the table numbers them (rankOf),
 *   null when unknown or several were approved; placedLast: the answer's ids that went last
 *   with no listing in Skybox's read (Smart spot only: a typed rank reads nothing from Skybox)
 * @returns {string}
 */
export function approveWhereText({ count, smart = false, beforeId = null, rank = null } = {}, answer = null) {
  const n = wholeFrom1(count) || 1;
  const typed = wholeFrom1(rank);
  if (!answer) {
    if (smart) return `${smartSpot(n)}: the ranks after ${n === 1 ? 'it' : 'them'} move up one and are repriced too`;
    if (beforeId != null) {
      return typed
        ? `at rank ${typed}: ranks ${typed} and up move up one and are repriced too`
        : 'at the rank picked: the ranks from there up move up one and are repriced too';
    }
    return `${typed ? `at rank ${typed}, last` : 'ranked last'}: no other rank moves`;
  }
  // A rank landed on only names one listing
  const landed = n === 1 ? wholeFrom1(answer.landed) : null;
  if (smart) return smartLandedText(n, landed, answer.placedLast);
  // The server inserts before beforeId (or appends): a ranking changed meanwhile lands it elsewhere
  if (landed && typed && landed !== typed) return `at rank ${landed} (the ranking had changed)`;
  const at = typed || landed;
  if (beforeId != null) return at ? `at rank ${at}` : 'at the rank picked';
  return at ? `at rank ${at}, last` : 'ranked last';
}

// Failed reviews that mean our copy of the groups and pending lists is
// stale: a 404 (the group is gone) and a 409 (NOT_PENDING: someone else
// handled them; RANKING_CHANGED: beforeId left the ladder)
const REFRESH_ON_REVIEW = Object.freeze([404, 409]);

/**
 * A failed approve / dismiss whose groups and matches must be re-read
 * (useReviewLionAutoPending). Not a 404 PRICING_NOT_FOUND: Skybox has no
 * pricing for the event, so the Smart spot could not be read and nothing
 * changed; a re-read would only ask Skybox again.
 * @param {object|null} err  the axios error
 * @returns {boolean}
 */
export function shouldRefreshOnReview(err) {
  if (!shouldRefreshOn(REFRESH_ON_REVIEW, err)) return false;
  return err.response?.data?.code !== 'PRICING_NOT_FOUND';
}
